/**
 * Les demandes de remboursement, côté administration.
 *
 * ## Ce que cet écran est, et ce qu'il n'est pas
 *
 * C'est une **liste de travail** : voici ce qu'il faut virer, à qui, et avec
 * quelle communication. Ce n'est pas un écran de paiement — le système ne signe
 * aucun virement, il constate ceux qui sont sortis.
 *
 * ## Comment une demande se referme
 *
 * **Toute seule, à l'import du CODA suivant.** La demande porte sa propre
 * communication structurée `900…` ; si l'administration la recopie sur le
 * virement, `refundMatching` la reconnaît, place le montant sur la provision de
 * la famille, et le déclencheur `refund_requests_sync_status` passe la demande
 * à `payee`.
 *
 * C'est la différence de nature avec les remboursements de notes de crédit :
 * ceux-là sont annotés à la main et lus par expression régulière, avec tous les
 * aléas que cela suppose — 11 communications sur 45 tronquées à 53 caractères.
 * Ici la communication est la nôtre, validée par la banque à la saisie.
 *
 * Si la communication n'a pas été recopiée, le virement tombe dans la file des
 * dépenses à placer et se relie à la main. Le chemin manuel reste, il n'est
 * simplement plus le chemin normal.
 */

import { db } from './db';

export type StatutDeDemande = 'en_attente' | 'payee' | 'refusee' | 'annulee';

export interface DemandeDeRemboursementAdmin {
  id: string;
  createdAt: string;
  userId: string;
  amountCents: number;
  status: StatutDeDemande;
  /** Les 12 chiffres, préfixe `900`. */
  communicationDigits: string;
  /** La forme lisible, à recopier sur le virement. */
  communication: string;
  iban: string | null;
  accountHolder: string | null;
  motif: string | null;
  /** Déjà placé par le rapprochement. */
  placedCents: number;
}

function formatStructuree(digits: string): string {
  return /^\d{12}$/.test(digits)
    ? `+++${digits.slice(0, 3)}/${digits.slice(3, 7)}/${digits.slice(7)}+++`
    : digits;
}

/** Combien de demandes attendent un virement, et pour quel montant. */
export async function countDemandesEnAttente(): Promise<{ nombre: number; cents: number }> {
  const { data, error } = await db
    .from('refund_requests')
    .select('amount_cents')
    .eq('status', 'en_attente');
  if (error) throw error;
  const lignes = (data ?? []) as unknown as { amount_cents: number }[];
  return {
    nombre: lignes.length,
    cents: lignes.reduce((s, l) => s + Number(l.amount_cents), 0),
  };
}

export async function loadDemandes(
  statuts: StatutDeDemande[] = ['en_attente'],
): Promise<DemandeDeRemboursementAdmin[]> {
  const { data, error } = await db
    .from('refund_requests')
    .select('id, created_at, user_id, amount_cents, status, communication_digits, iban, account_holder, motif')
    .in('status', statuts)
    /* La plus ancienne d'abord : une demande de remboursement qui attend est
       de l'argent qu'on doit, et l'ordre d'arrivée est le seul juste. */
    .order('created_at', { ascending: true });
  if (error) throw error;

  const lignes = (data ?? []) as unknown as Record<string, unknown>[];
  if (lignes.length === 0) return [];

  /* Ce qui a déjà été placé, en une requête pour toute la page plutôt qu'une
     par ligne — la file des paiements avait fait l'erreur inverse. */
  const { data: placements } = await db
    .from('refund_allocations')
    .select('refund_request_id, amount_cents')
    .in('refund_request_id', lignes.map((l) => String(l.id)));

  const parDemande = new Map<string, number>();
  for (const p of ((placements ?? []) as unknown as Record<string, unknown>[])) {
    const k = String(p.refund_request_id);
    parDemande.set(k, (parDemande.get(k) ?? 0) + Number(p.amount_cents));
  }

  return lignes.map((r) => ({
    id: String(r.id),
    createdAt: String(r.created_at),
    userId: String(r.user_id),
    amountCents: Number(r.amount_cents),
    status: r.status as StatutDeDemande,
    communicationDigits: String(r.communication_digits),
    communication: formatStructuree(String(r.communication_digits)),
    iban: (r.iban as string) ?? null,
    accountHolder: (r.account_holder as string) ?? null,
    motif: (r.motif as string) ?? null,
    placedCents: parDemande.get(String(r.id)) ?? 0,
  }));
}

/**
 * Refuse une demande : le montant **revient aux avoirs** de la famille.
 *
 * `payee` n'est pas atteignable ici, et la base le refuse explicitement : une
 * demande devient payée par le rapprochement du virement réellement sorti,
 * jamais par un clic. La poser à la main laisserait croire qu'un virement est
 * parti alors qu'il ne l'est pas.
 */
export async function refuserDemande(id: string, motif: string): Promise<void> {
  const { error } = await db.rpc('decider_demande_de_remboursement', {
    p_id: id,
    p_status: 'refusee',
    p_motif: motif,
  });
  if (error) throw error;
}
